// src/features/owner/sections/analytics/AnalyticsSection.tsx — shared analytics archetype.
// Used by 5 thin page wrappers: Overview, UserStatistics, Engagement, Growth, Retention.
import { useState } from 'react';
import styled from 'styled-components';
import { useOwnerAnalytics } from '@/hooks';
import { KpiCard, OwnerPanel, EmptyState, DateRangePicker } from '../../components';
import { ComboChart } from '../../charts';

const Strip = styled.div`
  display: grid;
  grid-template-columns: repeat(auto-fit, minmax(150px, 1fr));
  gap: 10px;
  margin-bottom: 12px;
`;
const Stat = styled.div`
  display: flex;
  justify-content: space-between;
  padding: 7px 0;
  border-top: 1px solid ${({ theme }) => theme.color.glassBorder};
  &:first-child { border-top: none; }
  font-size: ${({ theme }) => theme.fontSize.sm};
  color: ${({ theme }) => theme.color.textMuted};
`;

const TONES = ['brand', 'blue', 'mint', 'ember', 'magenta', 'teal'];

const PickerRow = styled.div`
  display: flex;
  justify-content: flex-end;
  margin-bottom: 12px;
`;

export function AnalyticsSection({ kind }: { kind: string }) {
  const [rangeDays, setRangeDays] = useState(7);
  const { data, loading, error } = useOwnerAnalytics(kind, rangeDays);
  if (loading) return <p style={{ opacity: 0.6 }}>Loading…</p>;
  if (error || !data) return <EmptyState icon="bar-chart-3" message={error ?? 'No data'} />;
  return (
    <>
      <PickerRow>
        <DateRangePicker valueDays={rangeDays} onChange={setRangeDays} />
      </PickerRow>
      <Strip>
        {data.kpis.map((k, i) => (
          <KpiCard
            key={k.key}
            icon="bar-chart-3"
            tone={TONES[i % TONES.length] ?? 'brand'}
            label={k.label}
            value={k.value}
            deltaPct={k.deltaPct}
            trend={k.trend}
          />
        ))}
      </Strip>
      <OwnerPanel title="Trend">
        <ComboChart points={data.series} ariaLabel={`${kind} trend`} />
      </OwnerPanel>
      <div style={{ height: 10 }} />
      <OwnerPanel title="Breakdown">
        {data.table.length === 0
          ? <EmptyState icon="activity" message="Not enough data yet" />
          : data.table.map((r) => (
            <Stat key={r.label}>
              <span>{r.label}</span>
              <b>{r.value}</b>
            </Stat>
          ))}
      </OwnerPanel>
    </>
  );
}
